﻿/**
 * Utility to parse raw HTML form embed code from any autoresponder
 * (AWeber, GetResponse, ActiveCampaign, Mailchimp, ConvertKit, Sendlane, etc.)
 */
export function parseHtmlFormEmbed(htmlString) {
  if (!htmlString || typeof htmlString !== 'string' || !htmlString.trim()) {
    return { error: 'Please paste your autoresponder HTML form code.' };
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString.trim(), 'text/html');
    const form = doc.querySelector('form');

    if (!form) {
      return {
        error: 'No <form> element detected. Please make sure you copy the complete HTML form embed code (starting with <form> and ending with </form>).'
      };
    }

    const rawAction = form.getAttribute('action') || '';
    const action = rawAction.trim();
    const method = (form.getAttribute('method') || 'POST').toUpperCase();

    if (!action) {
      return {
        error: 'The <form> element is missing an "action" URL. Autoresponders require an action URL to receive leads.'
      };
    }

    const inputs = Array.from(doc.querySelectorAll('input, select, textarea'));
    
    let emailField = '';
    let nameField = '';
    const hiddenFields = {};

    inputs.forEach(input => {
      const type = (input.getAttribute('type') || 'text').toLowerCase();
      const name = input.getAttribute('name') || '';
      const value = input.getAttribute('value') || '';

      if (!name) return;

      if (type === 'hidden') {
        hiddenFields[name] = value;
      } else if (type === 'email' || /email|e-mail|mail/i.test(name)) {
        if (!emailField) emailField = name;
      } else if (/first_name|fname|firstname|full_name|fullname|name/i.test(name)) {
        if (!nameField) nameField = name;
      }
    });

    // Fallback: if no email field was identified by type or name, pick the first non-hidden text input
    if (!emailField) {
      const candidate = inputs.find(i => {
        const t = (i.getAttribute('type') || 'text').toLowerCase();
        return t !== 'hidden' && t !== 'submit' && t !== 'button' && i.getAttribute('name');
      });
      if (candidate) {
        emailField = candidate.getAttribute('name');
      } else {
        emailField = 'email';
      }
    }

    // Auto-detect provider brand from action URL or input names
    let provider = 'Custom Autoresponder';
    const lowerAction = action.toLowerCase();
    const allNames = Object.keys(hiddenFields).concat([emailField, nameField]).join(' ').toLowerCase();

    if (lowerAction.includes('aweber.com') || allNames.includes('listname') || allNames.includes('meta_web_form_id')) {
      provider = 'AWeber';
    } else if (lowerAction.includes('getresponse.com') || allNames.includes('campaign_token')) {
      provider = 'GetResponse';
    } else if (lowerAction.includes('activehosted.com') || lowerAction.includes('activecampaign.com')) {
      provider = 'ActiveCampaign';
    } else if (lowerAction.includes('list-manage.com') || lowerAction.includes('mailchimp.com')) {
      provider = 'Mailchimp';
    } else if (lowerAction.includes('convertkit.com') || lowerAction.includes('ck.page')) {
      provider = 'ConvertKit';
    } else if (lowerAction.includes('sendlane.com')) {
      provider = 'Sendlane';
    } else if (lowerAction.includes('infusionsoft.com') || lowerAction.includes('keap.com')) {
      provider = 'Keap / Infusionsoft';
    } else if (lowerAction.includes('constantcontact.com')) {
      provider = 'Constant Contact';
    }

    return {
      success: true,
      provider,
      action,
      method,
      emailField: emailField || 'email',
      nameField: nameField || 'name',
      hiddenFields,
      hiddenCount: Object.keys(hiddenFields).length,
      rawHtml: htmlString.trim()
    };
  } catch (err) {
    return { error: 'Failed to parse form embed: ' + err.message };
  }
}
